import { useState, useCallback, useRef, useEffect } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { connectWithFacebook, connectWithInstagram, verifyTokenExchange } from "@/app/api/social-connect";
import { InstagramService } from "../services/instagram";
import { InstagramState, InstagramAccount } from "../types";
import { toast as sonnerToast } from "sonner";
import { createClient } from "@/auth/client";
import { sessionKeys } from "@/lib/sessionKeys";
import { useTranslation } from "react-i18next";

export function useInstagram() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();

  const indexingMessages = [
    t("account_indexing.instagram.checking"),
    t("account_indexing.instagram.analyzing"),
    t("account_indexing.instagram.gathering_insights"),
    t("account_indexing.instagram.learning_audience"),
    t("account_indexing.instagram.organizing"),
    t("account_indexing.instagram.finalizing")
  ];
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Core Instagram state
  const [state, setState] = useState<InstagramState>({
    isLoadingInstagram: true,
    isConnecting: false,
    instagramError: null,
    
    // Connection state
    connection: {
      hasDirectConnection: false,
      hasInstagramViaFacebook: false,
      currentConnectionType: null,
      directProfile: null,
      facebookProfile: null,
      accounts: []
    },
    
    // Authentication user
    authenticatedUser: null,
    showSuccessEffect: false,
    
    // Instagram account selection state
    instagramSelectionState: {
      needsSelection: false,
      availableAccounts: [],
      isLoadingAccounts: false,
      isSelecting: false,
      selectedAccountId: null,
      hasTriedFetching: false
    },
    
    // Data display state
    isIndexing: {},
    currentMessageIndex: {},
    messageTimerId: {},
    mainTimerId: {}
  });

  const instagramFetchAttemptedForUserId = useRef<string | null>(null);
  const tokenHandledRef = useRef<boolean>(false);
  const indexingStartedRef = useRef<Record<string, boolean>>({});
  const indexToastShownRef = useRef<Record<string, boolean>>({});
  const initialAutoIndexDoneRef = useRef<boolean>(false);

  // Update Instagram state helper
  const updateState = useCallback((updates: Partial<InstagramState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // Update connection state helper
  const updateConnectionState = useCallback((updates: Partial<InstagramState['connection']>) => {
    setState(prev => ({ 
      ...prev, 
      connection: { ...prev.connection, ...updates }
    }));
  }, []);

  // Fetch Instagram accounts and connection status
  const fetchInstagramData = useCallback(async () => {
    if (!user?.id) return;

    try {
      updateState({ isLoadingInstagram: true, instagramError: null });

      // Get current connection state
      const connectionState = await InstagramService.getConnectionState(user);
      
      updateConnectionState(connectionState);

      // Instagram account selection should only be triggered manually via onFetchInstagramAccounts
      // No automatic triggering based on connection state

    } catch (error: any) {
      console.error("Error fetching Instagram data:", error);
      updateState({ 
        instagramError: error.message || "Failed to load Instagram data",
        isLoadingInstagram: false 
      });
    } finally {
      updateState({ isLoadingInstagram: false });
      
    }
  }, [user, updateState, updateConnectionState]);

  // Connect with Instagram (direct)
  const connectInstagramDirect = useCallback(async () => {
    if (!user) {
      console.error("User not authenticated");
      return;
    }

    // If user already has Facebook connection, ask to disconnect first
    if (state.connection.hasInstagramViaFacebook) {
      console.warn("Instagram via Facebook Already Connected: Please disconnect your Facebook-based Instagram connection first.");
      return;
    }

    try {
      updateState({ isConnecting: true, instagramError: null });

      // These functions redirect the browser, so no return value to check
      await connectWithInstagram();
    } catch (error: any) {
      console.error("Error connecting Instagram:", error);
      updateState({ 
        instagramError: error.message || "Failed to connect Instagram",
        isConnecting: false 
      });
      
      console.error("Connection Failed:", error.message || "Failed to connect to Instagram");
    }
  }, [user, state.connection.hasInstagramViaFacebook, updateState]);

  // Connect with Instagram via Facebook
  const connectInstagramViaFacebook = useCallback(async () => {
    if (!user) {
      console.error("User not authenticated");
      return;
    }

    // If user already has direct connection, ask to disconnect first
    if (state.connection.hasDirectConnection) {
      console.warn("Direct Instagram Already Connected: Please disconnect your direct Instagram connection first.");
      return;
    }

    try {
      updateState({ isConnecting: true, instagramError: null });

      // These functions redirect the browser, so no return value to check
      await connectWithFacebook();
    } catch (error: any) {
      console.error("Error connecting Instagram via Facebook:", error);
      updateState({ 
        instagramError: error.message || "Failed to connect Instagram via Facebook",
        isConnecting: false 
      });
      
      console.error("Connection Failed:", error.message || "Failed to connect to Instagram via Facebook");
    }
  }, [user, state.connection.hasDirectConnection, updateState]);

  // Disconnect Instagram
  const disconnectInstagram = useCallback(async (connectionId?: string) => {
    if (!user?.id) {
      console.error("User not authenticated");
      return;
    }

    try {
      updateState({ isConnecting: true, instagramError: null });

      let result: { success: boolean; error: string | null };

      if (connectionId) {
        // Disconnect specific connection
        result = await InstagramService.disconnectInstagram(connectionId);
      } else if (state.connection.currentConnectionType) {
        // Disconnect by connection type
        result = await InstagramService.disconnectByConnectionType(user.id, state.connection.currentConnectionType);
      } else {
        // Disconnect all Instagram connections
        result = await InstagramService.disconnectAllInstagramConnections(user.id);
      }

      if (result.success) {
        // Clear session state
        InstagramService.setSessionDisconnected();

        // Clear cached Instagram accounts for Facebook connection (if any)
        try {
          const fbConnId = state.connection.facebookProfile?.connectionId;
          if (fbConnId) {
            localStorage.removeItem(`ig_fb_accounts_${fbConnId}`);
          }
        } catch {}

        // Reset state
        updateConnectionState({
          hasDirectConnection: false,
          hasInstagramViaFacebook: false,
          currentConnectionType: null,
          directProfile: null,
          facebookProfile: null,
          accounts: []
        });

        updateState({
          isIndexing: {},
          currentMessageIndex: {},
          messageTimerId: {},
          mainTimerId: {},
          instagramSelectionState: {
            needsSelection: false,
            availableAccounts: [],
            isLoadingAccounts: false,
            isSelecting: false,
            selectedAccountId: null,
            hasTriedFetching: false
          }
        });

        sonnerToast.success("Instagram account has been disconnected.");

        // Refresh the page to ensure clean state
        router.refresh();
      } else {
        throw new Error(result.error || "Failed to disconnect Instagram");
      }
    } catch (error: any) {
      console.error("Error disconnecting Instagram:", error);
      updateState({ instagramError: error.message || "Failed to disconnect Instagram" });
      
      sonnerToast.error(error.message || "Failed to disconnect Instagram");
    } finally {
      updateState({ isConnecting: false });
    }
  }, [user, state.connection.currentConnectionType, updateState, updateConnectionState, router]);

  // Instagram indexing functionality
  const triggerIndex = useCallback(async (accountId: string, connectionId: string) => {
    try {
      console.log('[IG POLL HOOK] triggerIndex:start', { accountId, connectionId });
      if (indexingStartedRef.current[accountId] || state.isIndexing[accountId]) {
        console.log('[IG POLL HOOK] triggerIndex:already-indexing-skip', { accountId });
        return;
      }
      indexingStartedRef.current[accountId] = true;

      // Clear any pre-existing message interval for safety (shouldn't exist due to guards)
      const existingInterval = state.messageTimerId[accountId];
      if (existingInterval) {
        try { clearInterval(existingInterval as any); } catch {}
      }
      console.log('[IG POLL HOOK] triggerIndex:cleared-existing-interval', { accountId });

      // Clear any pre-existing main timer as well
      const existingMainTimer = state.mainTimerId[accountId];
      if (existingMainTimer) {
        try { clearTimeout(existingMainTimer as any); } catch {}
      }
      console.log('[IG POLL HOOK] triggerIndex:cleared-existing-main-timer', { accountId });

      // Set UI to indexing
      setState(prev => ({
        ...prev,
        isIndexing: { ...prev.isIndexing, [accountId]: true },
        currentMessageIndex: { ...prev.currentMessageIndex, [accountId]: 0 }
      }));
      console.log('[IG POLL HOOK] triggerIndex:set-indexing-state', { accountId });

      // Notify globally that indexing has started for this connection
      try {
        window.dispatchEvent(new CustomEvent('ig-index-triggered', { detail: { connectionId } }));
      } catch {}

      // Start rotating indexing messages for the UI
      const msgInterval = setInterval(() => {
        setState(prev => {
          const current = prev.currentMessageIndex[accountId] || 0;
          const next = (current + 1) % indexingMessages.length;
          return {
            ...prev,
            currentMessageIndex: { ...prev.currentMessageIndex, [accountId]: next }
          };
        });
      }, 3000);
      setState(prev => ({
        ...prev,
        messageTimerId: { ...prev.messageTimerId, [accountId]: msgInterval as any }
      }));
      console.log('[IG POLL HOOK] triggerIndex:message-interval-started', { accountId });

      // Safety timeout in case polling takes long. Do NOT stop animation here.
      const safetyTimer = setTimeout(() => {
        console.warn('[IG POLL HOOK] triggerIndex:safety-timeout-fired', { accountId });
        // Keep indexing animation active and polling ongoing. Just inform the user.
        sonnerToast.info("Indexing is taking longer than expected. We'll keep working in the background and notify you when it's done.");
        // Optionally, we could schedule another reminder timer here if desired.
      }, 120000);
      console.log('[IG POLL HOOK] triggerIndex:safety-timer-set', { accountId });

      // Save timer id
      setState(prev => ({
        ...prev,
        mainTimerId: { ...prev.mainTimerId, [accountId]: safetyTimer }
      }));
      console.log('[IG POLL HOOK] triggerIndex:main-timer-id-saved', { accountId });

      console.log('[IG POLL HOOK] triggerIndex:starting-trigger-request', { accountId, connectionId });
      // Record the trigger start time to ensure we only accept a NEW social fetch result
      const triggerStartMs = Date.now();
      const triggerPromise = InstagramService.triggerInstagramIndex(accountId, connectionId)
        .then(res => {
          console.log('[IG POLL HOOK] triggerIndex:trigger-response', res);
          return res;
        })
        .catch(err => {
          console.error('[IG POLL HOOK] triggerIndex:trigger-error', err);
          return { success: false, error: err?.message } as any;
        });

      if (!indexToastShownRef.current[accountId]) {
        sonnerToast.success('Instagram data indexing has begun. This may take a few minutes.');
        indexToastShownRef.current[accountId] = true;
      } else {
      }

      // Poll for completion. Complete when social fetch reports success; index poll is non-blocking.
      try {
        console.log('[IG POLL HOOK] triggerIndex:start-social_fetch-poll', { connectionId });
        // Determine connectionType for filtering status lookups
        const account = state.connection.accounts.find(a => a.id === accountId);
        const connType = account?.connectionType as 'instagram_direct' | 'instagram_via_facebook' | undefined;
        const socialFetchPoll = InstagramService.waitForSocialFetchSuccess(
          { connectionId, connectionType: connType },
          { intervalMs: 10000, timeoutMs: 10 * 60 * 1000, requireNewSinceMs: triggerStartMs }
        );

        // Do NOT await trigger here. Start background handling once it resolves.
        triggerPromise
          .then((triggerRes) => {
            if (triggerRes?.success && triggerRes?.operationId) {
              console.log('[IG POLL HOOK] triggerIndex:start-index_operations-poll', { operationId: triggerRes.operationId });
              InstagramService
                .waitForIndexSuccess(triggerRes.operationId, { intervalMs: 30000, timeoutMs: 5 * 60 * 1000 })
                .then((res) => {
                  console.log('[IG POLL HOOK] triggerIndex:index-poll-completed', res);
                  return res;
                })
                .catch((err) => {
                  console.warn('[IG POLL HOOK] triggerIndex:index-poll-error (non-fatal)', err);
                  return null;
                });
            } else {
              console.warn('[IG POLL HOOK] triggerIndex:no-operation-id-from-trigger', triggerRes);
            }
          })
          .catch((err) => {
            console.warn('[IG POLL HOOK] triggerIndex:trigger-promise-rejected (non-fatal for UI stop)', err);
          });

        // Await social fetch first to end the animation promptly
        const socialResult: any = await socialFetchPoll;
        console.log('[IG POLL HOOK] triggerIndex:polling-resolved-social-only', { socialRes: socialResult });

        const successSet = new Set(['success', 'partial_success', 'succeeded', 'completed', 'done']);
        const socialSucceeded = successSet.has((socialResult?.status || '').toString().toLowerCase());

        // Social success is sufficient to consider the overall flow complete
        const canCompleteFromSocialOnly = socialSucceeded;

        if (canCompleteFromSocialOnly) {
          // Clear the safety timer and stop animation
          const toClear = state.mainTimerId[accountId] || safetyTimer;
          try { clearTimeout(toClear as any); } catch {}

          setState(prev => ({
            ...prev,
            isIndexing: { ...prev.isIndexing, [accountId]: false },
            messageTimerId: { ...prev.messageTimerId, [accountId]: null },
            mainTimerId: { ...prev.mainTimerId, [accountId]: null }
          }));

          // Clear message interval
          try {
            const msgTimer = state.messageTimerId[accountId] || msgInterval;
            if (msgTimer) clearInterval(msgTimer as any);
          } catch {}

          console.log('[IG POLL HOOK] triggerIndex:animation-stopped', { accountId });
          // Allow future indexing triggers for this account
          indexingStartedRef.current[accountId] = false;
          // Broadcast completion/update so other parts of the app can refresh cached status
          try {
            window.dispatchEvent(new CustomEvent('ig-index-completed', { detail: { connectionId, status: socialResult?.status || 'success' } }));
          } catch {}
          sonnerToast.success('Instagram indexing completed. Redirecting to chat...');
          if (user?.id) {
            router.push(`/chat/${user.id}`);
          } else {
            router.push('/chat');
          }
        } else {
          // Not complete yet; keep animating until safety timeout or subsequent success
          console.warn('[IG POLL HOOK] triggerIndex:not-complete-yet', { socialSucceeded });
          // We intentionally do not clear timers or flip isIndexing here.
        }
      } catch (pollErr: any) {
        console.error('Instagram indexing polling error', pollErr);
        // On failure, stop animation and surface error
        const toClear = state.mainTimerId[accountId] || safetyTimer;
        try { clearTimeout(toClear as any); } catch {}

        setState(prev => ({
          ...prev,
          isIndexing: { ...prev.isIndexing, [accountId]: false },
          messageTimerId: { ...prev.messageTimerId, [accountId]: null },
          mainTimerId: { ...prev.mainTimerId, [accountId]: null }
        }));

        // Clear message interval
        try {
          const msgTimer = state.messageTimerId[accountId] || msgInterval;
          if (msgTimer) clearInterval(msgTimer as any);
        } catch {}

        sonnerToast.error(pollErr?.message || 'Instagram indexing failed');
        indexingStartedRef.current[accountId] = false;
        // Broadcast failure so listeners can mark red
        try {
          window.dispatchEvent(new CustomEvent('ig-index-completed', { detail: { connectionId, status: 'error' } }));
        } catch {}
        return;
      }

    } catch (error: any) {
      console.error('Error triggering index:', error);
      setState(prev => ({
        ...prev,
        isIndexing: { ...prev.isIndexing, [accountId]: false },
        messageTimerId: { ...prev.messageTimerId, [accountId]: null }
      }));
      // Clear message interval if set
      try {
        const msgTimer = state.messageTimerId[accountId];
        if (msgTimer) clearInterval(msgTimer as any);
      } catch {}
      indexingStartedRef.current[accountId] = false;
      // Broadcast failure so listeners can mark red
      try {
        window.dispatchEvent(new CustomEvent('ig-index-completed', { detail: { connectionId, status: 'error' } }));
      } catch {}
      sonnerToast.error(error.message || 'Failed to start Instagram indexing');
    }
  }, [state, router]);

  // Helper function to auto-trigger indexing for newly connected accounts
  const autoTriggerIndexForNewAccounts = useCallback(async (newAccounts: InstagramAccount[]) => {

    // Only auto-trigger for accounts that aren't connection-only
    const indexableAccounts = newAccounts.filter(account => !account.isConnectionOnly);
    console.log("[AUTO-INDEX:IG] autoTriggerIndexForNewAccounts", { total: newAccounts.length, indexable: indexableAccounts.length });

    for (const account of indexableAccounts) {
      if (indexingStartedRef.current[account.id]) {
        console.log("[AUTO-INDEX:IG] already indexing, skipping", { accountId: account.id });
        continue;
      }
      console.log("[AUTO-INDEX:IG] triggering auto-index", { accountId: account.id, connectionId: account.connectionId });
      try { sessionStorage.setItem(sessionKeys.igIndexed(account.id), "true"); } catch {}
      try {
        await triggerIndex(account.id, account.connectionId);
      } catch (error) {
        console.error(`[AUTO-INDEX:IG] failed to auto-trigger index for account ${account.id}:`, error);
        // Don't throw - continue with other accounts
      }
    }
  }, [triggerIndex]);

  // Handle token exchange on redirect
  const handleTokenExchange = useCallback(async () => {
    if (!searchParams) return;
    
    // Extract URL params for OAuth callbacks
    const status = searchParams.get("status");
    const connection = searchParams.get("connection");
    const error = searchParams.get("error");
    const code = searchParams.get("code");
    const urlState = searchParams.get("state");

    

    if (error) {
      console.error("OAuth error:", error);
      updateState({ instagramError: `Authentication failed: ${error}` });
      return;
    }

    // Handle Instagram direct OAuth success redirect
    if (status === "success" && connection === "instagram_direct" && user) {
      if (tokenHandledRef.current) {
        return;
      }
      tokenHandledRef.current = true;
      try {
        updateState({ isConnecting: true });

        // Clear URL early to avoid repeated handling; only when on Account route
        if (pathname?.startsWith('/account')) {
          router.replace('/account');
        }

        const result = await verifyTokenExchange();

        if (result.success) {
          updateState({ showSuccessEffect: true });

          sonnerToast.success("Instagram account connected successfully!");

          // Store previous accounts before fetching new data
          const previousAccounts = state.connection.accounts;
          

          // Fetch updated data
          await fetchInstagramData();

          // Fetch fresh connection state to avoid stale closure over state
          const refreshedState = await InstagramService.getConnectionState(user);
          

          const newAccounts = refreshedState.accounts.filter((current: InstagramAccount) =>
            !previousAccounts.some((prev: InstagramAccount) => prev.id === current.id)
          );

          if (newAccounts.length > 0) {
            await autoTriggerIndexForNewAccounts(newAccounts);
          } else {
            
          }

          // URL already cleaned above
        } else {
          throw new Error(result.error || "Instagram connection failed");
        }
      } catch (error: any) {
        console.error("Instagram direct connection error:", error);
        updateState({ instagramError: error.message || "Failed to complete Instagram connection" });
        
        sonnerToast.error(error.message || "Failed to complete Instagram connection");
      } finally {
        updateState({ isConnecting: false });
      }
      return;
    }

    // Handle Threads OAuth success redirect - just clean up URL
    if (status === "success" && connection === "threads" && user) {
      if (tokenHandledRef.current) {
        return;
      }
      tokenHandledRef.current = true;
      
      // Clear URL early to avoid repeated handling; only when on Account route
      if (pathname?.startsWith('/account')) {
        router.replace('/account');
      }
      
      // No need to process Instagram data for Threads connection
      // The ThreadsSection component handles its own data loading
      return;
    }

    // Handle Facebook OAuth success redirect with Instagram selection needed
    if (status === "success" && connection === "facebook" && user) {
      if (tokenHandledRef.current) {
        return;
      }
      tokenHandledRef.current = true;
      try {
        updateState({ isConnecting: true });

        // Clear URL early to avoid repeated handling
        router.replace('/account');

        const result = await verifyTokenExchange();

        if (result.success && result.instagramViaFacebookConnected) {
          updateState({ showSuccessEffect: true });

          sonnerToast.success("Facebook connected successfully!");

          // Store previous accounts before fetching new data
          const previousAccounts = state.connection.accounts;
          

          // Fetch updated data
          await fetchInstagramData();

          // Fetch fresh connection state to avoid stale closure over state
          const refreshedState = await InstagramService.getConnectionState(user);
          

          const newAccounts = refreshedState.accounts.filter((current: InstagramAccount) =>
            !previousAccounts.some((prev: InstagramAccount) => prev.id === current.id)
          );

          if (newAccounts.length > 0) {
            await autoTriggerIndexForNewAccounts(newAccounts);
          } else {
            
          }

          // URL already cleaned above
        } else {
          throw new Error(result.error || "Facebook connection failed");
        }
      } catch (error: any) {
        console.error("Facebook connection error:", error);
        updateState({ instagramError: error.message || "Failed to complete Facebook connection" });
        
        sonnerToast.error(error.message || "Failed to complete Facebook connection");
      } finally {
        updateState({ isConnecting: false });
      }
      return;
    }

    // Handle regular Instagram OAuth redirect (with code/state)
    if (code && urlState && user) {
      try {
        updateState({ isConnecting: true });

        const result = await verifyTokenExchange();

        if (result.success) {
          updateState({ showSuccessEffect: true });

          sonnerToast.success("Your Instagram account has been connected.");

          // Store previous accounts before fetching new data
          const previousAccounts = state.connection.accounts;
          

          // Fetch updated data
          await fetchInstagramData();

          // Fetch fresh connection state
          const refreshedState = await InstagramService.getConnectionState(user);
          

          const newAccounts = refreshedState.accounts.filter((current: InstagramAccount) =>
            !previousAccounts.some((prev: InstagramAccount) => prev.id === current.id)
          );

          if (newAccounts.length > 0) {
            await autoTriggerIndexForNewAccounts(newAccounts);
          } else {
            
          }

          // Clean up URL; only when on Account route
          if (pathname?.startsWith('/account')) {
            router.replace('/account');
          }
        } else {
          throw new Error(result.error || "Token exchange failed");
        }
      } catch (error: any) {
        console.error("Token exchange error:", error);
        updateState({ instagramError: error.message || "Failed to complete authentication" });
        
        sonnerToast.error(error.message || "Failed to complete Instagram connection");
      } finally {
        updateState({ isConnecting: false });
      }
    }
  }, [searchParams, user, updateState, router, fetchInstagramData, state.connection.accounts, autoTriggerIndexForNewAccounts]);

  // Instagram account selection functions
  const fetchAvailableInstagramAccounts = useCallback(async () => {
    try {
      // Attempt to serve from cache first (until FB is disconnected)
      try {
        const fbConnId = state.connection.facebookProfile?.connectionId;
        if (fbConnId) {
          const cacheKey = `ig_fb_accounts_${fbConnId}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed?.accounts)) {
              setState(prev => ({
                ...prev,
                instagramSelectionState: {
                  ...prev.instagramSelectionState,
                  availableAccounts: parsed.accounts,
                  isLoadingAccounts: false,
                  hasTriedFetching: true,
                  needsSelection: true
                }
              }));
              return { success: true, accounts: parsed.accounts };
            }
          }
        }
      } catch {}

      setState(prev => ({
        ...prev,
        instagramSelectionState: {
          ...prev.instagramSelectionState,
          isLoadingAccounts: true
        }
      }));

      const response = await fetch('/api/facebook/instagram/accounts');
      const data = await response.json();

      if (response.ok) {
        setState(prev => ({
          ...prev,
          instagramSelectionState: {
            ...prev.instagramSelectionState,
            availableAccounts: data.accounts || [],
            isLoadingAccounts: false,
            hasTriedFetching: true,
            needsSelection: true
          }
        }));

        // Write to cache keyed by Facebook connection id so it persists until disconnect
        try {
          const fbConnId = data?.facebook_connection?.id || state.connection.facebookProfile?.connectionId;
          if (fbConnId) {
            const cacheKey = `ig_fb_accounts_${fbConnId}`;
            localStorage.setItem(cacheKey, JSON.stringify({ accounts: data.accounts || [] }));
          }
        } catch {}
        return { success: true, accounts: data.accounts || [] };
      } else {
        throw new Error(data.error || 'Failed to fetch Instagram accounts');
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        instagramSelectionState: {
          ...prev.instagramSelectionState,
          isLoadingAccounts: false,
          hasTriedFetching: true
        }
      }));
      console.error('Error fetching Instagram accounts:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const selectInstagramAccount = useCallback(async (accountId: string, facebookConnectionId: string) => {
    try {
      setState(prev => ({
        ...prev,
        instagramSelectionState: {
          ...prev.instagramSelectionState,
          isSelecting: true
        }
      }));

      const response = await fetch('/api/facebook/instagram/select', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instagramAccountIds: [accountId],
          facebookConnectionId
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setState(prev => ({
          ...prev,
          instagramSelectionState: {
            ...prev.instagramSelectionState,
            selectedAccountId: accountId,
            needsSelection: false,
            isSelecting: false
          }
        }));

        sonnerToast.success("Instagram account selected successfully!");
        
        // Refresh the Instagram data
        await fetchInstagramData();

        // Attempt auto-trigger for the selected account using fresh state
        if (user) {
          const refreshedState = await InstagramService.getConnectionState(user);
          const selected = refreshedState.accounts.find((a: InstagramAccount) => a.id === accountId);
          if (selected) {
            if (!selected.isConnectionOnly) {
              await autoTriggerIndexForNewAccounts([selected]);
            } else {
              // Selected account is connection-only; skipping auto-trigger
            }
          } else {
            // Selected account not found after refresh
          }
        }
        
        return { success: true };
      } else {
        throw new Error(data.error || 'Failed to select Instagram account');
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        instagramSelectionState: {
          ...prev.instagramSelectionState,
          isSelecting: false
        }
      }));
      console.error('Error selecting Instagram account:', error);
      sonnerToast.error(error.message || 'Failed to select Instagram account');
      return { success: false, error: error.message };
    }
  }, [fetchInstagramData]);

  const reopenInstagramSelection = useCallback(async () => {
    setState(prev => ({
      ...prev,
      instagramSelectionState: {
        ...prev.instagramSelectionState,
        needsSelection: true,
        hasTriedFetching: false
      }
    }));

    sonnerToast.success("Instagram account selection reopened.");
  }, []);

  const closeInstagramSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      instagramSelectionState: {
        ...prev.instagramSelectionState,
        needsSelection: false,
        isLoadingAccounts: false
      }
    }));
  }, []);

  // Effects
  useEffect(() => {
    
    if (!isLoading && user?.id) {
      // If OAuth params exist, handle them first and skip the initial fetch to avoid duplicate data loads
      const hasOAuthParams = !!(searchParams?.get('status') || (searchParams?.get('code') && searchParams?.get('state')) || searchParams?.get('error'));
      if (hasOAuthParams) {
        handleTokenExchange();
        return; // skip initial fetch when handling OAuth
      }

      // Initial fetch per user session
      if (instagramFetchAttemptedForUserId.current !== user.id) {
        instagramFetchAttemptedForUserId.current = user.id;
        fetchInstagramData();
      }
    }
  }, [user, isLoading, fetchInstagramData, handleTokenExchange, searchParams]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const supabase = createClient();
    const channel = supabase
      .channel(`instagram-connections:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'connected_social_accounts',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchInstagramData();
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ig_via_fb_connected_ig_accounts',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchInstagramData();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchInstagramData, user?.id]);

  // Check for session disconnection
  useEffect(() => {
    if (InstagramService.isSessionDisconnected()) {
      InstagramService.clearSessionDisconnected();
      fetchInstagramData();
    }
  }, [fetchInstagramData]);

  // Auto-index once per new browser session when accounts first load
  useEffect(() => {
    if (initialAutoIndexDoneRef.current) return;
    if (state.connection.accounts.length === 0) return;

    initialAutoIndexDoneRef.current = true;

    const indexableAccounts = state.connection.accounts.filter(a => !a.isConnectionOnly);
    for (const account of indexableAccounts) {
      const sessionKey = sessionKeys.igIndexed(account.id);
      try {
        if (sessionStorage.getItem(sessionKey) === "true") {
          console.log("[AUTO-INDEX:IG] already auto-indexed this session, skipping", { accountId: account.id });
          continue;
        }
      } catch {}
      if (indexingStartedRef.current[account.id]) {
        console.log("[AUTO-INDEX:IG] already indexing, skipping", { accountId: account.id });
        continue;
      }
      console.log("[AUTO-INDEX:IG] triggering auto-index (new session, first load)", { accountId: account.id });
      try { sessionStorage.setItem(sessionKey, "true"); } catch {}
      triggerIndex(account.id, account.connectionId);
    }
  }, [state.connection.accounts, triggerIndex]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(state.messageTimerId).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      Object.values(state.mainTimerId).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [state.messageTimerId, state.mainTimerId]);

  return {
    // State
    ...state,
    
    // Computed values
    isConnected: state.connection.currentConnectionType !== null,
    connectionType: state.connection.currentConnectionType,
    
    // Actions
    connectInstagramDirect,
    connectInstagramViaFacebook,
    disconnectInstagram,
    triggerIndex,
    refreshData: fetchInstagramData,
    
    // Utils
    indexingMessages,

    // Instagram account selection functions
    fetchAvailableInstagramAccounts,
    selectInstagramAccount,
    reopenInstagramSelection,
    closeInstagramSelection
  };
} 